/***
 *
 *   TICKET DETAILS HEADER
 *   Identity bar for the drawer: close, number, status / priority / overdue
 *   chips, the subject, and who opened it. Sync and copy-number sit on the
 *   right. Close calls pannel.hide (pannel hideClose is set when opening
 *   ticket details).
 *
 *   Replaces the old X | created-at | refresh strip: the chips and requester
 *   line were the first thing you looked for and they were buried a tab down.
 *
 **********/

import React, { useContext, useState } from 'react';
import Style from './nav.tailwind';
import {
    Button,
    Icon,
    Tooltip,
    UserAvatar,
    ViewContext,
    cn,
    initialsFromName,
} from 'components/lib';
import { useApiContext } from 'app/api';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslate } from 'app/translations';
import { Time } from '../history/general/time';
import { isTicketOverdue } from 'utilities/ticketDue';

const TicketDetailsNav = ({ ticket, status, requester, commentCount = 0 }) => {
    const { _t } = useTranslate();
    const viewContext = useContext(ViewContext);
    const { callApi } = useApiContext();
    const queryClient = useQueryClient();
    const [copied, setCopied] = useState(false);

    const service = ticket?.ticket_category?.integration_data?.service;

    const { mutate: syncTicketData, isLoading } = useMutation(
        'syncTicketData',
        () =>
            callApi({
                url: `/api/ticket/${ticket.id}/sync?service=${service}`,
                method: 'POST',
            }),
        {
            onSuccess: () => {
                queryClient.refetchQueries('tickets');
            },
        },
    );

    const copyNumber = async () => {
        try {
            await navigator.clipboard.writeText(ticket.number);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch (err) {
            // Clipboard unavailable (insecure context / denied) — nothing useful to say.
        }
    };

    const overdue = ticket
        ? isTicketOverdue({ due_date: ticket.due_date, status: status?.key })
        : false;
    const requesterName = requester?.creator_name || '';

    return (
        <header className={Style.header}>
            <div className={Style.topRow}>
                <Button
                    variant="ghost"
                    size="sm"
                    icon="x"
                    iconColor="currentColor"
                    ariaLabel={_t('cancel')}
                    action={() => viewContext.pannel.hide(true)}
                />

                {ticket ? (
                    <>
                        {/* Number, status and priority live in the properties rail — repeating
                            them here only added noise. Only the exceptions stay: overdue and
                            archived have no row of their own. */}
                        <div className={Style.chips}>
                            {overdue && (
                                <span className={Style.overdue}>
                                    <Icon
                                        image="alert-triangle"
                                        size={12}
                                        color="currentColor"
                                    />
                                    {_t('ticket.detail.overdue')}
                                </span>
                            )}
                            {ticket.is_archived && (
                                <span className={Style.archived}>
                                    <Icon image="archive" size={12} color="currentColor" />
                                    {_t('archived')}
                                </span>
                            )}
                        </div>

                        <div className={Style.actions}>
                            <Tooltip
                                label={
                                    copied
                                        ? _t('ticket.detail.numberCopied')
                                        : _t('ticket.detail.copyNumber')
                                }
                                side="bottom">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    icon={copied ? 'check' : 'copy'}
                                    iconColor="currentColor"
                                    ariaLabel={_t('ticket.detail.copyNumber')}
                                    action={copyNumber}
                                />
                            </Tooltip>

                            <Tooltip label={_t('sync.label')} side="bottom">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    icon="refresh-cw"
                                    iconColor="currentColor"
                                    iconClassName={isLoading ? 'animate-spin' : undefined}
                                    ariaLabel={_t('sync.label')}
                                    action={service ? syncTicketData : undefined}
                                    disabled={!service || isLoading}
                                />
                            </Tooltip>
                        </div>
                    </>
                ) : (
                    <span className={Style.chips} />
                )}
            </div>

            {ticket && (
                <div className={Style.identity}>
                    <h2 className={Style.subject}>{ticket.subject}</h2>

                    <div className={Style.meta}>
                        {requesterName && (
                            <span className={Style.metaItem}>
                                <UserAvatar
                                    name={requesterName}
                                    initials={initialsFromName(requesterName)}
                                    size="sm"
                                    className={Style.metaAvatar}
                                    fallbackClassName="text-[9px]"
                                />
                                <span className={Style.metaText}>
                                    {_t('ticket.detail.requestedBy')}{' '}
                                    <span className={Style.metaStrong}>{requesterName}</span>
                                </span>
                            </span>
                        )}

                        {requesterName && <span aria-hidden>·</span>}

                        <span className={Style.metaItem}>
                            {_t('ticket.detail.opened')}{' '}
                            <Time date={ticket.date_created} className={Style.metaTime} />
                        </span>

                        {commentCount > 0 && (
                            <>
                                <span aria-hidden>·</span>
                                <span className={cn(Style.metaItem, 'gap-1')}>
                                    <Icon
                                        image="message-square"
                                        size={12}
                                        color="currentColor"
                                    />
                                    {commentCount}{' '}
                                    {commentCount === 1
                                        ? _t('ticket.detail.commentOne')
                                        : _t('ticket.detail.commentMany')}
                                </span>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};

export default TicketDetailsNav;
